/**
 * A finding that stops the routine playing. Reported by throwing, which ends
 * the step's remaining checks — acceptable for a blocking finding, since the
 * author has to act on this one before anything else matters.
 */
export type ActionRequired = {
  kind:
    | 'invalidConfiguration'
    | 'missingConfiguration'
    | 'missingEquipment'
    // A copied example-routine placeholder survived into a real routine.
    // Distinct from missingEquipment: the remedy is selecting this cell's
    // device, not wiring one up.
    | 'examplePlaceholder'
    // A position value only the cell can supply is absent: an untaught
    // space item, an ungenerated grid, an empty inline target. The
    // awaiting-teaching state of a correctly built routine — distinct from
    // invalidConfiguration so consumers that tolerate exactly this family
    // (the goldens precheck gate, the eval scorers) can key on the code.
    | 'needsTeaching';
  message: string;
  fieldId?: string;
};

/**
 * A finding that does not stop the routine playing. Reported by handing it to
 * the `warn` collector, never by throwing: a throw would end the step's
 * remaining checks and let this hide a blocking finding one of them would have
 * raised.
 *
 * Separate from `ActionRequired` so the channel carries the tier. Nothing
 * chooses a severity, so nothing can choose the wrong one — throwing a warning
 * and warning an error are both type errors.
 */
export type WarningFinding = {
  kind: 'deprecatedConfigurationWarning' | 'danglingTooltip' | 'draftSkill';
  message: string;
  fieldId?: string;
  deprecationKind?: 'javaScriptExpressions';
};

export class ActionRequiredError extends Error {
  public actionRequired: ActionRequired;

  public constructor(actionRequired: ActionRequired) {
    super(actionRequired.message);
    this.actionRequired = actionRequired;
  }
}
